import { startTransition, useEffect, useState } from 'react';

export const useScreenDetector = () => {
  // Default to 1200 (Desktop) to match Server Side Rendering default
  const [width, setWidth] = useState(1200);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    // Mark as hydrated after a small delay to prevent hydration mismatch
    const hydrationTimer = setTimeout(() => {
      startTransition(() => {
        setIsHydrated(true);
      });
    }, 150);

    return () => clearTimeout(hydrationTimer);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    // Update width on client side mount
    const handleWindowSizeChange = () => {
      // Use startTransition for state updates that might trigger suspense boundaries
      startTransition(() => {
        setWidth((prevWidth) => {
          const newWidth = window.innerWidth;
          if (prevWidth !== newWidth) {
            return newWidth;
          }
          return prevWidth;
        });
      });
    };

    // Set initial width after hydration
    if (typeof window !== 'undefined') {
      handleWindowSizeChange();
      window.addEventListener('resize', handleWindowSizeChange);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleWindowSizeChange);
      }
    };
  }, [isHydrated]);

  const isMobile = width <= 768;
  const isTabletOnly = width <= 1024 && width > 768;
  const isTablet = width <= 1024;
  const isTabletMin = width <= 992;
  const isDesktop = width > 1024;

  // Provide deterministic initial state consistent with SSR (desktop default)
  // But force update via useEffect if client differs.
  // Using a hydration-safe pattern:
  // Render Desktop first (state=1200), then effect updates it.
  // This causes a flash of desktop layout on mobile but prevents hydration mismatch errors.

  return { isMobile, isTablet, isDesktop, isTabletMin, isTabletOnly };
};
